"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateMatchRank = void 0;
var helpers_1 = require("./helpers");
/**
 * Generates the ranking.
 * @param match The match of which the players need to be ranked
 * @param matchRules The rules of the match
 * @param setRules The rules for every set
 * @returns The ranking sorted according to the table tennis rules.
 */
function generateMatchRank(match, matchRules, setRules) {
    var rankedPlayers = splitRankedAndUnranked(match).rankedPlayers;
    var playerRanks = rankedPlayers.map(function (x) {
        return {
            id: x.id,
            player: x.player,
            points: 0,
            sameRankPoints: 0,
            sameRankGameRatio: helpers_1.WinLoseRatio.Zero,
            sameRankScoreRatio: helpers_1.WinLoseRatio.Zero,
            sameRankGameRatioEvery: helpers_1.WinLoseRatio.Zero,
            sameRankScoreRatioEvery: helpers_1.WinLoseRatio.Zero,
            sharedWith: [],
        };
    });
    var steps = [
        // Step 1: Set
        {
            set: "between",
            resetChange: function (rank) { return (rank.points = 0); },
            applyChange: function (rank, mod) { return (rank.points += mod.points); },
            groupAndSortBy: function (k) { return k.points; },
        },
        // Step 2: Set (between themselves)
        {
            set: "between",
            resetChange: function (rank) { return (rank.sameRankPoints = 0); },
            applyChange: function (rank, mod) { return (rank.sameRankPoints += mod.points); },
            groupAndSortBy: function (k) { return k.sameRankPoints; },
        },
        //  Step 3: Game W/L ratio (between themselves)
        {
            set: "between",
            resetChange: function (rank) { return (rank.sameRankGameRatio = helpers_1.WinLoseRatio.Zero); },
            applyChange: function (rank, mod) {
                return (rank.sameRankGameRatio = helpers_1.WinLoseRatio.sum(rank.sameRankGameRatio, mod.gameRatio));
            },
            groupAndSortBy: function (k) { return k.sameRankGameRatio.ratio; },
        },
        //  Step 4: Score W/L Ratio (between themselves)
        {
            set: "between",
            resetChange: function (rank) { return (rank.sameRankScoreRatio = helpers_1.WinLoseRatio.Zero); },
            applyChange: function (rank, mod) {
                return (rank.sameRankScoreRatio = helpers_1.WinLoseRatio.sum(rank.sameRankScoreRatio, mod.scoreRatio));
            },
            groupAndSortBy: function (k) { return k.sameRankScoreRatio.ratio; },
        },
        //  Step 5: Game W/L ratio (including other sets)
        {
            set: "every",
            resetChange: function (rank) { return (rank.sameRankGameRatioEvery = helpers_1.WinLoseRatio.Zero); },
            applyChange: function (rank, mod) {
                return (rank.sameRankGameRatioEvery = helpers_1.WinLoseRatio.sum(rank.sameRankGameRatioEvery, mod.gameRatio));
            },
            groupAndSortBy: function (k) { return k.sameRankGameRatioEvery.ratio; },
        },
        //  Step 6: Score W/L ratio (including other sets)
        {
            set: "every",
            resetChange: function (rank) { return (rank.sameRankScoreRatioEvery = helpers_1.WinLoseRatio.Zero); },
            applyChange: function (rank, mod) {
                return (rank.sameRankScoreRatioEvery = helpers_1.WinLoseRatio.sum(rank.sameRankScoreRatioEvery, mod.scoreRatio));
            },
            groupAndSortBy: function (k) { return k.sameRankScoreRatioEvery.ratio; },
        },
    ];
    var result = {
        ranked: [],
        rankedSets: filterSets(match.getSets(), "between", rankedPlayers.map(function (x) { return x.id; }), setRules),
    };
    generateMatchRankStep(result, playerRanks, match, matchRules, setRules, steps, 0);
    return result;
}
exports.generateMatchRank = generateMatchRank;
function generateMatchRankStep(result, remaining, match, matchRules, setRules, steps, stepIndex) {
    var _a, _b;
    if (remaining.length <= 1) {
        (_a = result.ranked).push.apply(_a, remaining);
        return;
    }
    if (stepIndex >= steps.length) {
        // No more steps, players share the same rank.
        remaining.forEach(function (x) {
            var _a;
            var otherPlayerIds = remaining
                .map(function (y) { return y.id; })
                .filter(function (y) { return y !== x.id; });
            (_a = x.sharedWith).push.apply(_a, otherPlayerIds);
        });
        (_b = result.ranked).push.apply(_b, remaining);
        return;
    }
    var step = steps[stepIndex];
    var sets = filterSets(match.getSets(), step.set, remaining.map(function (x) { return x.id; }), setRules);
    remaining.forEach(function (rank) {
        step.resetChange(rank);
    });
    var pointChanges = getPointChanges(sets, matchRules, setRules);
    remaining.forEach(function (rank) {
        pointChanges
            .filter(function (x) { return x.id === rank.id; })
            .forEach(function (mod) { return step.applyChange(rank, mod); });
    });
    remaining.sort(function (a, b) { return step.groupAndSortBy(b) - step.groupAndSortBy(a); });
    var grouped = helpers_1.groupBy(remaining, function (x) { return step.groupAndSortBy(x); }, function (x) { return x; });
    if (grouped.length > 1) {
        // The first group can continue.
        // The other groups, restart at step 2 (index =1)
        for (var i = 0; i < grouped.length; ++i) {
            var group = grouped[i];
            generateMatchRankStep(result, group.values, match, matchRules, setRules, steps, 1);
        }
    }
    else {
        grouped.forEach(function (group) {
            generateMatchRankStep(result, group.values, match, matchRules, setRules, steps, stepIndex + 1);
        });
    }
}
function filterSets(sets, filter, playerIds, setRules) {
    var completeSets = sets.filter(function (x) { return (x.set.walkover || helpers_1.getSetWinner(x.set, setRules)) != undefined; });
    return completeSets.filter(function (ms) {
        if (filter === "between") {
            return (playerIds.includes(ms.awayPlayerId) &&
                playerIds.includes(ms.homePlayerId));
        }
        else {
            return (playerIds.includes(ms.awayPlayerId) ||
                playerIds.includes(ms.homePlayerId));
        }
    });
}
function splitRankedAndUnranked(match) {
    // Only players that caused a walk over can be removed from the match.
    var canBeStrikedPlayers = [];
    match.getSets().forEach(function (x) {
        if (x.set.walkover == "home" &&
            !canBeStrikedPlayers.includes(x.awayPlayerId)) {
            canBeStrikedPlayers.push(x.awayPlayerId);
        }
        if (x.set.walkover == "away" &&
            !canBeStrikedPlayers.includes(x.homePlayerId)) {
            canBeStrikedPlayers.push(x.homePlayerId);
        }
    });
    var strikePlayers = [];
    canBeStrikedPlayers.forEach(function (pId) {
        var setsWithPlayer = match
            .getSets()
            .filter(function (x) { return x.homePlayerId == pId || x.awayPlayerId == pId; });
        var playedSets = setsWithPlayer.filter(function (x) { return x.set.walkover === undefined; }).length;
        var notEnoughPlayedSets = playedSets <= setsWithPlayer.length / 2;
        if (notEnoughPlayedSets) {
            strikePlayers.push(pId);
        }
    });
    var rankedPlayers = match
        .getPlayers()
        .filter(function (p) { return !strikePlayers.includes(p.id); });
    var unrankedPlayers = match
        .getPlayers()
        .filter(function (p) { return strikePlayers.includes(p.id); });
    return { unrankedPlayers: unrankedPlayers, rankedPlayers: rankedPlayers };
}
function getPointChanges(rankedSets, matchRules, setRules) {
    var pointChanges = [];
    rankedSets.forEach(function (matchSet) {
        var games = matchSet.set.games;
        var gamesWonByHome = games.filter(function (x) { return helpers_1.getGameWinner(x, setRules.gameRules) === "home"; }).length;
        var gamesWonByAway = games.filter(function (x) { return helpers_1.getGameWinner(x, setRules.gameRules) === "away"; }).length;
        var winner = helpers_1.getSetWinner(matchSet.set, setRules);
        if (matchSet.set.walkover) {
            // In a walkover only the victor gets points
            if (matchSet.set.walkover === "home") {
                pointChanges.push({
                    id: matchSet.homePlayerId,
                    points: matchRules.victoryPoints,
                    gameRatio: helpers_1.WinLoseRatio.Zero,
                    scoreRatio: helpers_1.WinLoseRatio.Zero,
                });
            }
            if (matchSet.set.walkover === "away") {
                pointChanges.push({
                    id: matchSet.awayPlayerId,
                    points: matchRules.victoryPoints,
                    gameRatio: helpers_1.WinLoseRatio.Zero,
                    scoreRatio: helpers_1.WinLoseRatio.Zero,
                });
            }
        }
        else {
            if (winner == "home") {
                pointChanges.push({
                    id: matchSet.homePlayerId,
                    points: matchRules.victoryPoints,
                    gameRatio: new helpers_1.WinLoseRatio(gamesWonByHome, gamesWonByAway),
                    scoreRatio: new helpers_1.WinLoseRatio(games.reduce(function (pv, cv) { return pv + cv.homeScore; }, 0), games.reduce(function (pv, cv) { return pv + cv.awayScore; }, 0)),
                });
                pointChanges.push({
                    id: matchSet.awayPlayerId,
                    points: matchRules.defeatPoints,
                    gameRatio: new helpers_1.WinLoseRatio(gamesWonByAway, gamesWonByHome),
                    scoreRatio: new helpers_1.WinLoseRatio(games.reduce(function (pv, cv) { return pv + cv.awayScore; }, 0), games.reduce(function (pv, cv) { return pv + cv.homeScore; }, 0)),
                });
            }
            if (winner == "away") {
                pointChanges.push({
                    id: matchSet.awayPlayerId,
                    points: matchRules.victoryPoints,
                    gameRatio: new helpers_1.WinLoseRatio(gamesWonByAway, gamesWonByHome),
                    scoreRatio: new helpers_1.WinLoseRatio(games.reduce(function (pv, cv) { return pv + cv.awayScore; }, 0), games.reduce(function (pv, cv) { return pv + cv.homeScore; }, 0)),
                });
                pointChanges.push({
                    id: matchSet.homePlayerId,
                    points: matchRules.defeatPoints,
                    gameRatio: new helpers_1.WinLoseRatio(gamesWonByHome, gamesWonByAway),
                    scoreRatio: new helpers_1.WinLoseRatio(games.reduce(function (pv, cv) { return pv + cv.homeScore; }, 0), games.reduce(function (pv, cv) { return pv + cv.awayScore; }, 0)),
                });
            }
        }
    });
    return pointChanges;
}
